import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";

export const LoginContext = createContext();

export const LoginProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem("isLoggedIn");
    return stored === "true";
  });

  const [userId, setUserId] = useState(() => {
    return localStorage.getItem("userId") || null;
  });

  const [roleName, setRoleName] = useState(() => {
    return localStorage.getItem("roleName") || null;
  });

  const [userName, setUserName] = useState(() => {
    return localStorage.getItem("userName") || null;
  });

  const [isLoading, setIsLoading] = useState(true);

  // Ref to store the timeout ID
  const logoutTimerRef = useRef(null);

  // Inactivity timeout duration (1 hour = 3600000 milliseconds)
  const INACTIVITY_TIMEOUT = 3600000; // 1 hour

  // Check localStorage on mount
  useEffect(() => {
    const storedIsLoggedIn = localStorage.getItem("isLoggedIn");
    const storedUserId = localStorage.getItem("userId");
    const storedRoleName = localStorage.getItem("roleName");
    const storedUserName = localStorage.getItem("userName");

    if (storedIsLoggedIn === "true" && storedUserId) {
      setIsLoggedIn(true);
      setUserId(storedUserId);
      setRoleName(storedRoleName);
      setUserName(storedUserName);
    }
    setIsLoading(false);
  }, []);

  const logout = useCallback(() => {
    setIsLoggedIn(false);
    setUserId(null);
    setRoleName(null);
    setUserName(null);

    // Clear localStorage
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("userId");
    localStorage.removeItem("roleName");
    localStorage.removeItem("userName");
    localStorage.removeItem("authToken");
    localStorage.removeItem("redirectAfterLogin");

    // Clear the timer
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  }, []);

  const resetLogoutTimer = useCallback(() => {
    // Clear existing timer
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
    }

    // Set new timer only if user is logged in
    if (isLoggedIn) {
      logoutTimerRef.current = setTimeout(() => {
        logout();
        // Optionally show a message to the user
        alert("You have been logged out due to inactivity.");
      }, INACTIVITY_TIMEOUT);
    }
  }, [isLoggedIn, logout]);

  // Set up activity listeners
  useEffect(() => {
    if (!isLoggedIn) return;

    // Activity events to track
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    // Reset timer on any activity
    const handleActivity = () => {
      resetLogoutTimer();
    };

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, handleActivity);
    });

    // Initialize timer
    resetLogoutTimer();

    // Cleanup
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
      }
    };
  }, [isLoggedIn, resetLogoutTimer]);

  const login = (userId, roleName, userName) => {
    setIsLoggedIn(true);
    setUserId(userId);
    setRoleName(roleName);
    setUserName(userName);

    // Save to localStorage
    localStorage.setItem("isLoggedIn", "true");
    localStorage.setItem("userId", userId);
    localStorage.setItem("roleName", roleName);
    if (userName) {
      localStorage.setItem("userName", userName);
    }

    // Start inactivity timer
    resetLogoutTimer();
  };

  return (
    <LoginContext.Provider
      value={{
        isLoggedIn,
        userId,
        roleName,
        userName,
        login,
        logout,
        isLoading,
      }}
    >
      {children}
    </LoginContext.Provider>
  );
};
